import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { PhoneCall, RefreshCw, Save } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Loader } from '@/components/ui/Loader';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { useStore } from '@/store/useStore';
import {
  dialCall,
  fetchExtensions,
  fetchInboundRoutes,
  fetchIVRs,
  fetchMenuOptions,
  fetchOutboundRoutes,
  fetchQueues,
  getInboundRoute,
  updateInboundRoute,
} from '@/services/api';
import type {
  InboundRoute,
  InboundRouteOverrideAttempt,
  MenuOption,
  RouteDestinationInfo,
} from '@/types';
import toast from 'react-hot-toast';
import { showErrorToast } from '@/utils/toast';

const GLOBAL_OVERRIDE_CODE = '*99';

type DestinationDefinition = {
  value: string;
  label: string;
  inputMode: 'lookup' | 'direct' | 'none';
};

const DESTINATION_DEFINITIONS: DestinationDefinition[] = [
  { value: 'extension', label: 'Extension', inputMode: 'lookup' },
  { value: 'ext_vm', label: 'Extension Voicemail', inputMode: 'lookup' },
  { value: 'queue', label: 'Queue', inputMode: 'lookup' },
  { value: 'ivr', label: 'IVR', inputMode: 'lookup' },
  { value: 'ring_group', label: 'Ring Group', inputMode: 'lookup' },
  { value: 'call_flow', label: 'Call Flow', inputMode: 'lookup' },
  { value: 'conference', label: 'Conference', inputMode: 'lookup' },
  { value: 'group_vm', label: 'Group Voicemail', inputMode: 'lookup' },
  { value: 'outroute', label: 'Outbound Route', inputMode: 'lookup' },
  { value: 'ai_receptionist', label: 'AI Receptionist', inputMode: 'lookup' },
  { value: 'external_num', label: 'External Number', inputMode: 'direct' },
  { value: 'play_greeting', label: 'Play Greeting', inputMode: 'direct' },
  { value: 'end_call', label: 'Hang Up', inputMode: 'none' },
];

function getDestinationDefinition(type: string): DestinationDefinition | undefined {
  return DESTINATION_DEFINITIONS.find((item) => item.value === type);
}

function normalizeRouteDestination(route: InboundRoute | undefined): RouteDestinationInfo {
  return (
    route?.normalized_default_destination || {
      type: '',
      value: '',
      prefix: '',
    }
  );
}

function normalizeBusinessDestination(route: InboundRoute | undefined): RouteDestinationInfo {
  return (
    route?.normalized_business_hours_destination || {
      type: '',
      value: '',
      prefix: '',
    }
  );
}

function buildInboundRouteUpdatePayload(
  route: InboundRoute,
  destination: {
    type: string;
    value: string;
    prefix: string;
  }
): Partial<InboundRoute> & { id: number } {
  const payload: Partial<InboundRoute> & { id: number } = {
    id: route.id,
    name: route.name,
    alert_info: route.alert_info || '',
    def_dest: destination.type,
    def_dest_value: destination.value,
  };

  if (route.trunk_list?.length) payload.trunk_list = route.trunk_list;
  if (route.did_option) payload.did_option = route.did_option;
  if (route.did_pattern_list?.length) payload.did_pattern_list = route.did_pattern_list;
  if (route.did_pattern_to_ext) payload.did_pattern_to_ext = route.did_pattern_to_ext;
  if (route.did_to_ext_start) payload.did_to_ext_start = route.did_to_ext_start;
  if (route.did_to_ext_end) payload.did_to_ext_end = route.did_to_ext_end;
  if (route.did_pattern_to_ext_list?.length) {
    payload.did_pattern_to_ext_list = route.did_pattern_to_ext_list;
  }
  if (route.cid_option) payload.cid_option = route.cid_option;
  if (route.cid_pattern_list?.length) payload.cid_pattern_list = route.cid_pattern_list;
  if (route.cid_to_phonebook) payload.cid_to_phonebook = route.cid_to_phonebook;

  if (destination.type === 'external_num' || destination.type === 'play_greeting') {
    payload.def_dest_prefix = destination.prefix.trim();
  } else {
    payload.def_dest_prefix = '';
  }

  if (destination.type === 'pattern_to_ext') {
    payload.def_dest_ext_list = route.def_dest_ext_list || [];
  }

  return payload;
}

export function InboundRoutingManager({ readOnly = false }: { readOnly?: boolean }) {
  const {
    inboundRoutes,
    setInboundRoutes,
    extensions,
    setExtensions,
    ivrs,
    setIVRs,
    queues,
    setQueues,
    loading,
    setLoading,
    callerExtension,
    setCallerExtension,
  } = useStore();

  const [selectedRouteId, setSelectedRouteId] = useState<string>('');
  const [destinationType, setDestinationType] = useState('');
  const [destinationValue, setDestinationValue] = useState('');
  const [destinationPrefix, setDestinationPrefix] = useState('');
  const [saving, setSaving] = useState(false);
  const [overrideBusy, setOverrideBusy] = useState(false);
  const [overrideDialVerified, setOverrideDialVerified] = useState(false);
  const [overrideAttempts, setOverrideAttempts] = useState<
    Record<number, InboundRouteOverrideAttempt>
  >({});
  const [menuOptions, setMenuOptions] = useState<Record<string, MenuOption[]>>({
    ring_group: [],
    call_flow: [],
    conference: [],
    group_vm: [],
    outroute: [],
    ai_receptionist: [],
  });

  useEffect(() => {
    if (overrideDialVerified && !callerExtension) {
      setOverrideDialVerified(false);
    }
  }, [callerExtension, overrideDialVerified]);

  useEffect(() => {
    const nextRoutes = inboundRoutes.map((route) => {
      const eligible =
        Boolean(callerExtension) && route.route_mode === 'time_condition_global_default';
      const attempt = overrideAttempts[route.id] || route.last_override_attempt || 'idle';

      if (
        route.one_click_override_eligible === eligible &&
        route.last_override_attempt === attempt
      ) {
        return route;
      }

      return {
        ...route,
        one_click_override_eligible: eligible,
        last_override_attempt: attempt,
      };
    });

    const changed = nextRoutes.some((route, index) => route !== inboundRoutes[index]);
    if (changed) {
      setInboundRoutes(nextRoutes);
    }
  }, [callerExtension, inboundRoutes, overrideAttempts, setInboundRoutes]);

  const loadData = useCallback(async () => {
    setLoading('routes', true);

    try {
      const [routes, exts, ivrsData, queuesData, ringGroups, callFlows, conferences, groupVms, outboundRoutes, aiReceptionists] =
        await Promise.all([
          fetchInboundRoutes(),
          fetchExtensions(),
          fetchIVRs(),
          fetchQueues(),
          fetchMenuOptions('ring_group'),
          fetchMenuOptions('call_flow'),
          fetchMenuOptions('conference'),
          fetchMenuOptions('group_vm'),
          fetchOutboundRoutes(),
          fetchMenuOptions('ai_receptionist'),
        ]);

      setInboundRoutes(routes);
      setExtensions(exts);
      setIVRs(ivrsData);
      setQueues(queuesData);
      setMenuOptions({
        ring_group: ringGroups,
        call_flow: callFlows,
        conference: conferences,
        group_vm: groupVms,
        outroute: outboundRoutes,
        ai_receptionist: aiReceptionists,
      });
    } catch (error: unknown) {
      console.error('Error loading inbound routing data:', error);
      showErrorToast(error, 'Failed to load inbound routes', 'inbound-routes-load-error');
    } finally {
      setLoading('routes', false);
    }
  }, [setInboundRoutes, setExtensions, setIVRs, setLoading, setQueues]);

  const mergeRoute = useCallback((updatedRoute: InboundRoute) => {
    setInboundRoutes(
      inboundRoutes.map((route) => (route.id === updatedRoute.id ? updatedRoute : route))
    );
  }, [inboundRoutes, setInboundRoutes]);

  const loadRouteDetails = useCallback(async (routeId: number) => {
    try {
      const route = await getInboundRoute(routeId);
      if (route) {
        mergeRoute(route);
      }
    } catch (error) {
      console.warn(`Unable to fetch details for inbound route ${routeId}:`, error);
    }
  }, [mergeRoute]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!selectedRouteId && inboundRoutes.length > 0) {
      setSelectedRouteId(String(inboundRoutes[0].id));
    }
  }, [inboundRoutes, selectedRouteId]);

  useEffect(() => {
    if (!selectedRouteId) return;

    const route = inboundRoutes.find((item) => String(item.id) === selectedRouteId);
    if (!route) return;

    const destination = normalizeRouteDestination(route);
    setDestinationType(destination.type);
    setDestinationValue(destination.value);
    setDestinationPrefix(destination.prefix || '');
  }, [selectedRouteId, inboundRoutes]);

  useEffect(() => {
    if (!selectedRouteId) return;

    void loadRouteDetails(Number(selectedRouteId));
  }, [loadRouteDetails, selectedRouteId]);

  const selectedRoute = inboundRoutes.find((route) => String(route.id) === selectedRouteId);

  const lookupOptions = useMemo(() => {
    const extensionOptions = extensions.map((ext) => ({
      value: ext.id,
      label: `Ext ${ext.number}${ext.display_name ? ` (${ext.display_name})` : ''}`,
    }));

    return {
      extension: extensionOptions,
      ext_vm: extensionOptions.map((ext) => ({
        value: ext.value,
        label: `Voicemail for ${ext.label}`,
      })),
      queue: queues.map((queue) => ({
        value: queue.id,
        label: queue.name,
      })),
      ivr: ivrs.map((ivr) => ({
        value: ivr.id,
        label: ivr.name,
      })),
      ring_group: menuOptions.ring_group.map((item) => ({
        value: item.value,
        label: item.text,
      })),
      call_flow: menuOptions.call_flow.map((item) => ({
        value: item.value,
        label: item.text,
      })),
      conference: menuOptions.conference.map((item) => ({
        value: item.value,
        label: item.text,
      })),
      group_vm: menuOptions.group_vm.map((item) => ({
        value: item.value,
        label: item.text,
      })),
      outroute: menuOptions.outroute.map((item) => ({
        value: item.value,
        label: item.text,
      })),
      ai_receptionist: menuOptions.ai_receptionist.map((item) => ({
        value: item.value,
        label: item.text,
      })),
    };
  }, [extensions, ivrs, menuOptions, queues]);

  const selectableDestinationTypes = useMemo(() => {
    return DESTINATION_DEFINITIONS.filter((definition) => {
      if (definition.inputMode !== 'lookup') return true;
      const options = lookupOptions[definition.value as keyof typeof lookupOptions] || [];
      return options.length > 0 || destinationType === definition.value;
    });
  }, [destinationType, lookupOptions]);

  const resolveDestinationLabel = useCallback((destination?: RouteDestinationInfo) => {
    if (!destination?.type) return 'Not configured';

    switch (destination.type) {
      case 'end_call':
        return 'Hang Up';
      case 'external_num':
        return `${destination.prefix || ''}${destination.value}`.trim() || 'External Number';
      case 'play_greeting':
        return destination.value
          ? `Play ${destination.value}${destination.prefix ? ` (${destination.prefix}x)` : ''}`
          : 'Play Greeting';
      default: {
        const options = lookupOptions[destination.type as keyof typeof lookupOptions] || [];
        const option = options.find((item) => item.value === destination.value);
        if (option) return option.label;
        return `${getDestinationDefinition(destination.type)?.label || destination.type}${
          destination.value ? ` ${destination.value}` : ''
        }`.trim();
      }
    }
  }, [lookupOptions]);

  const currentDestinationLabel = useMemo(() => {
    if (!selectedRoute) return '—';
    return resolveDestinationLabel(normalizeRouteDestination(selectedRoute));
  }, [resolveDestinationLabel, selectedRoute]);

  const businessHoursLabel = useMemo(() => {
    if (!selectedRoute) return '—';
    return resolveDestinationLabel(normalizeBusinessDestination(selectedRoute));
  }, [resolveDestinationLabel, selectedRoute]);

  const activeDestinationDefinition = getDestinationDefinition(destinationType);
  const currentRouteMode = selectedRoute?.route_mode || 'unknown_capability';
  const isStandardEditable = currentRouteMode === 'standard_editable';
  const isGlobalOverride = currentRouteMode === 'time_condition_global_default';
  const isCustomUnsupported = currentRouteMode === 'time_condition_custom_unsupported';

  const getSelectedLookupOptions = () => {
    if (!activeDestinationDefinition || activeDestinationDefinition.inputMode !== 'lookup') {
      return [];
    }

    return lookupOptions[activeDestinationDefinition.value as keyof typeof lookupOptions] || [];
  };

  const canEditSelectedRoute =
    isStandardEditable &&
    (!!activeDestinationDefinition &&
      (activeDestinationDefinition.inputMode !== 'lookup' ||
        getSelectedLookupOptions().length > 0 ||
        !destinationType));

  const handleSave = async () => {
    if (!selectedRoute) {
      toast.error('Please select a route to update');
      return;
    }

    if (!isStandardEditable) {
      toast.error('This route cannot be edited directly through the API');
      return;
    }

    const definition = getDestinationDefinition(destinationType);
    if (!definition) {
      toast.error('Please choose a supported destination type');
      return;
    }

    if (definition.inputMode === 'lookup' && !destinationValue) {
      toast.error('Please choose a destination');
      return;
    }

    if (definition.value === 'external_num' && !destinationValue.trim()) {
      toast.error('Please enter the external number');
      return;
    }

    if (definition.value === 'play_greeting' && !destinationValue.trim()) {
      toast.error('Please enter the prompt file name');
      return;
    }

    if (definition.value === 'play_greeting' && destinationPrefix) {
      const count = Number(destinationPrefix);
      if (!Number.isInteger(count) || count < 1 || count > 5) {
        toast.error('Playback count must be between 1 and 5');
        return;
      }
    }

    setSaving(true);

    try {
      const payload = buildInboundRouteUpdatePayload(selectedRoute, {
        type: definition.value,
        value: definition.inputMode === 'none' ? '' : destinationValue.trim(),
        prefix: destinationPrefix,
      });

      const success = await updateInboundRoute(payload);

      if (!success) {
        toast.error('Failed to update inbound route');
        return;
      }

      toast.success('Inbound route updated');
      await loadData();
      await loadRouteDetails(selectedRoute.id);
    } catch (error: unknown) {
      console.error('Error updating inbound route:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update inbound route');
    } finally {
      setSaving(false);
    }
  };

  const updateRouteAttempt = (routeId: number, status: InboundRouteOverrideAttempt) => {
    setOverrideAttempts((current) => ({
      ...current,
      [routeId]: status,
    }));
  };

  const runOverrideDial = async (action: 'verify' | 'toggle' | 'clear') => {
    if (!callerExtension) {
      toast.error('Please save a default operator extension first');
      return;
    }

    setOverrideBusy(true);

    try {
      const result = await dialCall(callerExtension, GLOBAL_OVERRIDE_CODE, {
        autoAnswer: true,
      });

      if (!result.success) {
        if (selectedRoute) {
          updateRouteAttempt(selectedRoute.id, 'failed');
        }
        toast.error('Override dial failed. Use the manual *99 fallback for this route.');
        return;
      }

      setOverrideDialVerified(true);
      if (selectedRoute) {
        updateRouteAttempt(selectedRoute.id, 'success');
      }

      if (action === 'verify') {
        toast.success('One-click override verified for this PBX');
      } else if (action === 'clear') {
        toast.success('Clear override request sent');
      } else {
        toast.success('Override toggle request sent');
      }
    } catch (error: unknown) {
      if (selectedRoute) {
        updateRouteAttempt(selectedRoute.id, 'failed');
      }
      console.error('Error dialing override feature code:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Override dial failed. Use the manual *99 fallback for this route.'
      );
    } finally {
      setOverrideBusy(false);
    }
  };

  const getModeBadge = (route: InboundRoute | undefined) => {
    if (!route) return null;

    switch (route.route_mode) {
      case 'standard_editable':
        return <Badge variant="success">Editable by API</Badge>;
      case 'time_condition_global_default':
        return <Badge variant="info">Global Override Mode</Badge>;
      case 'time_condition_custom_unsupported':
        return <Badge variant="warning">Custom Time Condition</Badge>;
      default:
        return <Badge variant="danger">Read-only</Badge>;
    }
  };

  if (loading.routes && inboundRoutes.length === 0) {
    return (
      <Card>
        <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white mb-6">
          Inbound Routing
        </h2>
        <Loader text="Loading inbound routes..." />
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Inbound Routing
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Direct route updates for standard routes and controlled override handling for
            business-hours routes
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={loadData}
          isLoading={loading.routes}
        >
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {inboundRoutes.length === 0 ? (
        <p className="text-center text-gray-500 dark:text-gray-400 py-8">
          No inbound routes found
        </p>
      ) : (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Route
            </label>
            <select
              value={selectedRouteId}
              onChange={(event) => setSelectedRouteId(event.target.value)}
              className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
            >
              {inboundRoutes.map((route) => (
                <option key={route.id} value={route.id}>
                  {route.name || `Route ${route.id}`}
                </option>
              ))}
            </select>
          </div>

          {selectedRoute && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                {getModeBadge(selectedRoute)}
                {selectedRoute.one_click_override_eligible && (
                  <Badge variant="success">One-click eligible</Badge>
                )}
                {selectedRoute.last_override_attempt === 'failed' && (
                  <Badge variant="warning">Last override attempt failed</Badge>
                )}
                {selectedRoute.last_override_attempt === 'success' && (
                  <Badge variant="success">Last override attempt succeeded</Badge>
                )}
              </div>

              <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg space-y-3">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Current Route Target</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                    {currentDestinationLabel}
                  </p>
                </div>

                {isGlobalOverride && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Business Hours
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                        {businessHoursLabel}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Outside Business Hours
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                        {currentDestinationLabel}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {isStandardEditable ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Destination Type
                      </label>
                      <select
                        value={destinationType}
                        onChange={(event) => {
                          setDestinationType(event.target.value);
                          setDestinationValue('');
                          setDestinationPrefix('');
                        }}
                        className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Select destination type</option>
                        {selectableDestinationTypes.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {activeDestinationDefinition?.inputMode === 'lookup' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Destination
                        </label>
                        <select
                          value={destinationValue}
                          onChange={(event) => setDestinationValue(event.target.value)}
                          className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                        >
                          <option value="">Select destination</option>
                          {getSelectedLookupOptions().map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {destinationType === 'external_num' && (
                      <>
                        <Input
                          label="Dial Prefix"
                          value={destinationPrefix}
                          onChange={(event) => setDestinationPrefix(event.target.value)}
                          placeholder="Optional prefix"
                        />
                        <Input
                          label="External Number"
                          value={destinationValue}
                          onChange={(event) => setDestinationValue(event.target.value)}
                          placeholder="e.g. +27111234567"
                        />
                      </>
                    )}

                    {destinationType === 'play_greeting' && (
                      <>
                        <Input
                          label="Playback Count"
                          type="number"
                          min="1"
                          max="5"
                          value={destinationPrefix}
                          onChange={(event) => setDestinationPrefix(event.target.value)}
                          placeholder="1"
                        />
                        <Input
                          label="Prompt File Name"
                          value={destinationValue}
                          onChange={(event) => setDestinationValue(event.target.value)}
                          placeholder="e.g. office_closed.wav"
                        />
                      </>
                    )}

                    {destinationType === 'end_call' && (
                      <div className="md:col-span-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 dark:text-gray-400">
                        Hang Up selected. No destination value is required.
                      </div>
                    )}
                  </div>

                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    DID, Caller ID, and trunk matching remain read-only in this screen.
                  </div>

                  {!readOnly && (
                    <div className="flex items-center justify-end gap-3">
                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Button
                          variant="primary"
                          size="md"
                          onClick={handleSave}
                          isLoading={saving}
                          disabled={!selectedRouteId || !destinationType || !canEditSelectedRoute}
                        >
                          <Save size={16} className="mr-2" />
                          Update Route
                        </Button>
                      </motion.div>
                    </div>
                  )}
                </div>
              ) : isGlobalOverride ? (
                <div className="space-y-6">
                  <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4 space-y-4">
                    <div>
                      <p className="text-sm font-medium text-primary-900 dark:text-primary-200">
                        Time-condition override mode
                      </p>
                      <p className="text-sm text-primary-800 dark:text-primary-300 mt-1">
                        This route appears to follow global/default business hours. The app does
                        not edit the business-hours branches directly. It can only attempt the
                        documented global override code <span className="font-semibold">{GLOBAL_OVERRIDE_CODE}</span>.
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-2">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Manual fallback
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      If one-click dialing is unavailable or fails, dial{' '}
                      <span className="font-semibold">{GLOBAL_OVERRIDE_CODE}</span> from an
                      authorized extension.
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Yeastar uses the same code to toggle and clear global business-hours override.
                    </p>
                  </div>
                </div>
              ) : isCustomUnsupported ? (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <p className="text-sm font-medium text-yellow-900 dark:text-yellow-200">
                    Custom time-condition route detected
                  </p>
                  <p className="text-sm text-yellow-800 dark:text-yellow-300 mt-1">
                    This route appears to use custom business hours or custom time periods. Yeastar
                    generates per-route feature codes for these routes, and the current documented
                    API does not expose those codes. The app keeps this route read-only in v1.
                  </p>
                </div>
              ) : (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <p className="text-sm font-medium text-red-900 dark:text-red-200">
                    Route capability is unclear
                  </p>
                  <p className="text-sm text-red-800 dark:text-red-300 mt-1">
                    This route does not fit a documented editable or globally overridable pattern.
                    The app leaves it read-only to avoid sending unsupported updates.
                  </p>
                </div>
              )}

              <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-lg p-4 space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Global Business Hours Override
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Use the documented global feature code <span className="font-semibold">{GLOBAL_OVERRIDE_CODE}</span> for
                    routes that rely on the PBX's default business-hours schedule. This helper is
                    always available because some PBXs do not expose enough route metadata for the
                    app to classify the selected route reliably.
                  </p>
                </div>

                <Input
                  label="Default Operator Extension"
                  value={callerExtension || ''}
                  onChange={(event) => setCallerExtension(event.target.value)}
                  placeholder="e.g. 1001"
                />

                <div className="flex flex-wrap gap-3">
                  {!overrideDialVerified ? (
                    <Button
                      variant="secondary"
                      size="md"
                      onClick={() => void runOverrideDial('verify')}
                      isLoading={overrideBusy}
                      disabled={!callerExtension}
                    >
                      <PhoneCall size={16} className="mr-2" />
                      Verify One-Click Override
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="primary"
                        size="md"
                        onClick={() => void runOverrideDial('toggle')}
                        isLoading={overrideBusy}
                        disabled={!callerExtension}
                      >
                        <PhoneCall size={16} className="mr-2" />
                        Dial *99 Now
                      </Button>
                      <Button
                        variant="secondary"
                        size="md"
                        onClick={() => void runOverrideDial('clear')}
                        isLoading={overrideBusy}
                        disabled={!callerExtension}
                      >
                        <PhoneCall size={16} className="mr-2" />
                        Dial *99 Again
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}
